// Local-timezone date helpers.
//
// Background: `todayLocal()` returns the UTC date,
// which is off-by-one for anyone west of UTC after their local ~4pm–8pm. This
// module standardises on the user's LOCAL calendar day so a milk log saved at
// 8pm Central shows today, not tomorrow.

/** Local YYYY-MM-DD for a given Date (defaults to now). */
export function toLocalDateStr(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Convenience alias for the most common call. */
export const todayLocal = () => toLocalDateStr();

/** N days ago (or ahead if negative) as a local YYYY-MM-DD. */
export function daysAgoLocal(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toLocalDateStr(d);
}

/**
 * Format a stored YYYY-MM-DD (or ISO) as the user's short locale date, e.g.
 * "Feb 22, 2026". Returns the original string on parse failure so we never
 * blank the UI. Intentionally passes month/day/year components — NOT a Date
 * built from the raw string — to sidestep the UTC-midnight parsing quirk.
 */
export function formatDateShort(input, locale = undefined) {
  if (!input) return '';
  const s = String(input).slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return input;
  const [, y, mo, d] = m;
  const dt = new Date(Number(y), Number(mo) - 1, Number(d));
  if (isNaN(dt.getTime())) return input;
  try {
    return dt.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return input;
  }
}

/** True when a stored YYYY-MM-DD matches today in the local timezone. */
export function isTodayLocal(input) {
  return String(input).slice(0, 10) === todayLocal();
}
